import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

function Landing() {
  const navigate = useNavigate();

  const stats = [
    {
      value: '+$10M',
      label: 'Invertidos en fondos para Ciencia, Tecnología e Innovación',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      value: '+5700',
      label: 'Investigadores y docentes beneficiados mediante los servicios especializados',
      color: 'from-purple-500 to-pink-500'
    },
    {
      value: '+$900K',
      label: 'Gestados para financiamiento de proyectos de investigación y financiamiento social',
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-950 overflow-hidden">
      
      {/* Hero Section */}
      <section className="relative min-h-[calc(100vh-64px)] flex items-center">
        <div className="absolute inset-0">
          {/* Geometric Background Elements */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.1, scale: 1 }}
            transition={{ duration: 1 }}
            className="absolute top-1/4 right-0 w-96 h-96 bg-primary rounded-full blur-3xl"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="absolute bottom-1/4 left-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.05, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500 rounded-full blur-3xl"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Logo CEDIA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <img 
                src="/images/cedia.webp" 
                alt="CEDIA" 
                className="h-16 w-auto object-contain drop-shadow-lg"
              />
            </motion.div>

            <div className="space-y-4">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white leading-tight"
              >
                Postula y lleva tu{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-600 to-pink-600">
                  investigación más lejos
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg md:text-xl text-slate-600 dark:text-slate-300"
              >
                Convocatoria abierta hasta el 31 de agosto
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <button
                onClick={() => navigate('/proyecto')}
                className="px-8 py-4 bg-gradient-to-r from-primary to-purple-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center gap-2"
              >
                <span>Revisa las bases aquí</span>
                <span className="material-icons-round">arrow_forward</span>
              </button>
              
              <button
                onClick={() => navigate('/dashboards')}
                className="px-8 py-4 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm text-slate-900 dark:text-white text-lg font-semibold rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-primary dark:hover:border-primary shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Explorar Datos
              </button>
            </motion.div>

            {/* Features Pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-3"
            >
              <div className="flex items-center gap-2 px-4 py-2 bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-full border border-slate-200 dark:border-slate-700">
                <span className="material-icons-round text-green-500 text-sm">verified</span>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Datos Confiables
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-full border border-slate-200 dark:border-slate-700">
                <span className="material-icons-round text-blue-500 text-sm">insights</span>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Análisis con IA
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-full border border-slate-200 dark:border-slate-700">
                <span className="material-icons-round text-purple-500 text-sm">school</span>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  12 Universidades
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Image Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-primary p-8">
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
              
              <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                <div className="space-y-6">
                  {/* Header */}
                  <div className="text-center space-y-2">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-semibold">
                      <span className="material-icons-round text-sm">campaign</span>
                      CONVOCATORIA ABIERTA
                    </div>
                    <h3 className="text-3xl font-bold text-white">
                      Fondo CEDIA<br/>DIVULGA
                    </h3>
                  </div>

                  {/* Illustration/Image Placeholder */}
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl p-8 aspect-video flex items-center justify-center">
                    <span className="material-icons-round text-white text-9xl opacity-50">science</span>
                  </div>

                  {/* Stats in Card */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-white">$10M+</div>
                      <div className="text-xs text-white/80 mt-1">Invertidos</div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-white">5700+</div>
                      <div className="text-xs text-white/80 mt-1">Investigadores</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="group relative"
              >
                <div className="relative glass-panel rounded-2xl p-8 border border-white/20 dark:border-slate-700 overflow-hidden">
                  {/* Gradient Background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <div className="relative space-y-4">
                    <div className={`text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${stat.color}`}>
                      {stat.value}
                    </div>
                    <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                      {stat.label}
                    </p>
                  </div>

                  {/* Icon Decoration */}
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 opacity-10">
                    <span className="material-icons-round text-9xl text-slate-400">
                      {index === 0 ? 'attach_money' : index === 1 ? 'groups' : 'trending_up'}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              ¿Qué puedes hacer en SIGC?
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Explora, analiza y visualiza la producción científica de las universidades ecuatorianas
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: 'explore',
                title: 'Explorar Investigadores',
                description: 'Descubre investigadores, sus métricas y publicaciones',
                link: '/investigadores'
              },
              {
                icon: 'article',
                title: 'Ver Publicaciones',
                description: 'Accede a la producción científica indexada',
                link: '/publicaciones'
              },
              {
                icon: 'dashboard',
                title: 'Analizar Datos',
                description: 'Visualiza estadísticas y dashboards interactivos',
                link: '/dashboards'
              },
              {
                icon: 'search',
                title: 'Buscar en APIs',
                description: 'Consulta OpenAlex y otras fuentes externas',
                link: '/busqueda-externa'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.03, y: -5 }}
                onClick={() => navigate(feature.link)}
                className="glass-panel rounded-xl p-6 border border-white/20 dark:border-slate-700 hover:border-primary/50 transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="material-icons-round text-white text-2xl">
                    {feature.icon}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  {feature.description}
                </p>
                <div className="flex items-center gap-2 text-primary text-sm font-semibold group-hover:gap-3 transition-all">
                  <span>Explorar</span>
                  <span className="material-icons-round text-sm">arrow_forward</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative glass-panel rounded-3xl p-12 border border-white/20 dark:border-slate-700 overflow-hidden"
          >
            {/* Background Gradients */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-purple-500/10 to-pink-500/10"></div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl"></div>
            
            <div className="relative text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
                ¿Listo para explorar el conocimiento científico?
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                Accede a la plataforma completa y descubre insights sobre la investigación en Ecuador
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => navigate('/proyecto')}
                  className="px-8 py-4 bg-gradient-to-r from-primary to-purple-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  Conocer más del proyecto
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="px-8 py-4 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-lg font-semibold rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-primary dark:hover:border-primary shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  Iniciar sesión
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <div className="flex justify-center items-center gap-4 mb-4">
            <img 
              src="/images/cedia.webp" 
              alt="CEDIA" 
              className="h-12 w-auto object-contain opacity-80"
            />
          </div>
          <p className="text-slate-600 dark:text-slate-400">
            Proyecto financiado por los Fondos I+D+i de CEDIA
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-500">
            Corporación Ecuatoriana para el Desarrollo de la Investigación y la Academia
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-600">
            © 2026 CEDIA. Todos los derechos reservados.
          </p>
        </div>
      </footer>

    </div>
  );
}

export default Landing;
